import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { apiErrorMessage } from "@/lib/api/errors";

import { cascadeDelete } from "../api";
import { useCascadeStore } from "../store";

/** Mot à saisir pour armer le bouton. Volontairement pénible à taper par réflexe. */
const CONFIRM_WORD = "SUPPRIMER";

/**
 * Suppression en cascade — réservée au super éditeur GoldenTech.
 *
 * Montée une seule fois dans le shell applicatif : la modale s'ouvre d'elle-même
 * quand le serveur vient de refuser une suppression ET que le compte a le droit
 * de passer outre (cf. features/cascade/offer.ts).
 *
 * Deux garde-fous, parce que l'opération touche des dossiers médicaux et des
 * documents financiers :
 * 1. l'inventaire exhaustif de ce qui part est affiché AVANT toute action ;
 * 2. le bouton reste inerte tant que « SUPPRIMER » n'est pas saisi.
 */
export function CascadeDeleteDialog() {
  const target = useCascadeStore((s) => s.target);
  const dismiss = useCascadeStore((s) => s.dismiss);
  const [typed, setTyped] = useState("");
  const queryClient = useQueryClient();

  useEffect(() => {
    setTyped("");
  }, [target?.id]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!target) throw new Error("Aucune cible");
      return cascadeDelete(target.resource, target.id);
    },
    onSuccess: () => {
      const total = target?.preview.total ?? 0;
      // Une cascade traverse patients, ventes, paiements, stock… Invalider
      // largement coûte un rechargement ; invalider trop peu laisserait des
      // écrans afficher des enregistrements qui n'existent plus.
      void queryClient.invalidateQueries();
      toast.success(
        `Supprimé avec ${total} enregistrement${total > 1 ? "s" : ""} rattaché${total > 1 ? "s" : ""}.`,
      );
      dismiss();
    },
    onError: (err) =>
      toast.error(apiErrorMessage(err, "La suppression en cascade a échoué.")),
  });

  if (!target) return null;
  const { preview } = target;
  const armed = typed.trim().toUpperCase() === CONFIRM_WORD;

  return (
    <Dialog open onOpenChange={(open) => !open && !mutation.isPending && dismiss()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Supprimer avec tout ce qui en dépend
          </DialogTitle>
          <DialogDescription>
            La suppression de <strong>{preview.target_label}</strong> est refusée
            parce que des données y sont rattachées. Votre niveau d'accès éditeur
            permet de passer outre.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div>
            <p className="text-sm font-medium">
              {preview.total} enregistrement{preview.total > 1 ? "s" : ""} rattaché
              {preview.total > 1 ? "s" : ""} seront supprimés :
            </p>
            <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
              {preview.items.map((item) => (
                <li key={item.type} className="flex justify-between gap-4">
                  <span>{item.count > 1 ? item.label_plural : item.label}</span>
                  <span className="font-medium tabular-nums">{item.count}</span>
                </li>
              ))}
            </ul>
          </div>

          {preview.warnings.length > 0 && (
            <div className="border border-destructive/40 bg-destructive/5 p-3">
              <ul className="space-y-2 text-sm text-destructive">
                {preview.warnings.map((w) => (
                  <li key={w}>{w}</li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-muted-foreground">
            Rien n'est effacé de la base : l'opération reste réversible et elle
            est consignée au journal d'audit avec la liste complète des éléments
            emportés.
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="cascade-confirm">
              Saisissez <strong>{CONFIRM_WORD}</strong> pour confirmer
            </Label>
            <Input
              id="cascade-confirm"
              value={typed}
              autoComplete="off"
              onChange={(e) => setTyped(e.target.value)}
              disabled={mutation.isPending}
            />
          </div>
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={dismiss} disabled={mutation.isPending}>
            Annuler
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate()}
            disabled={!armed || mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Trash2 className="h-4 w-4" />
            )}
            Supprimer les {preview.total + 1} éléments
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
